import { useMemo } from "react";
import { TFunction } from "i18next";
import { formatMonthDayOrdinal } from "@/utils/datetime";
import {
  DayOfQuarter,
  DayOfWeek,
  DayOfYear,
  Frequency,
  PeriodFieldConfig,
  PeriodFieldOption
} from "@/components/global/Chat/components/ScheduleModal/types";

const TRANSLATION_KEY = "chat.scheduleModal";

const buildYearConfig = (
  t: TFunction,
  fiscalStartMonth: number | null
): PeriodFieldConfig => {
  const calendarYearOptions: PeriodFieldOption[] = [
    {
      id: DayOfYear.FIRST,
      displayName: t(`${TRANSLATION_KEY}.yearDays.first`),
      subtitle: formatMonthDayOrdinal(new Date(Date.UTC(2024, 0, 1)))
    },
    {
      id: DayOfYear.LAST,
      displayName: t(`${TRANSLATION_KEY}.yearDays.last`),
      subtitle: formatMonthDayOrdinal(new Date(Date.UTC(2024, 11, 31)))
    }
  ];

  const hasNonDefaultFiscalYear =
    fiscalStartMonth !== null && fiscalStartMonth !== 0;

  if (!hasNonDefaultFiscalYear || fiscalStartMonth === null) {
    return {
      labelKey: "dayOfYear",
      defaultValue: DayOfYear.FIRST,
      options: calendarYearOptions
    };
  }

  return {
    labelKey: "dayOfYear",
    defaultValue: DayOfYear.FIRST,
    fixedOptions: calendarYearOptions,
    options: [
      {
        id: DayOfYear.FIRST_FISCAL,
        displayName: t(`${TRANSLATION_KEY}.yearDays.fiscalFirst`),
        subtitle: formatMonthDayOrdinal(
          new Date(Date.UTC(2024, fiscalStartMonth, 1))
        )
      },
      {
        id: DayOfYear.LAST_FISCAL,
        displayName: t(`${TRANSLATION_KEY}.yearDays.fiscalLast`),
        subtitle: formatMonthDayOrdinal(
          new Date(Date.UTC(2024, fiscalStartMonth, 0))
        )
      }
    ]
  };
};

export const usePeriodFieldByFrequency = (
  t: TFunction,
  fiscalStartMonth: number | null
): Record<Frequency, PeriodFieldConfig | null> =>
  useMemo(
    () => ({
      [Frequency.DAY]: null,
      [Frequency.WEEK]: {
        labelKey: "dayOfWeek",
        defaultValue: DayOfWeek.MONDAY,
        options: Object.values(DayOfWeek).map((value) => ({
          id: value,
          displayName: t(`${TRANSLATION_KEY}.days.${value}`)
        }))
      },
      [Frequency.MONTH]: {
        labelKey: "dayOfMonth",
        defaultValue: "1",
        options: Array.from({ length: 31 }, (_, i) => String(i + 1)).map(
          (day) => ({ id: day, displayName: day })
        )
      },
      [Frequency.QUARTER]: {
        labelKey: "dayOfQuarter",
        defaultValue: DayOfQuarter.FIRST,
        options: Object.values(DayOfQuarter).map((value) => ({
          id: value,
          displayName: t(`${TRANSLATION_KEY}.quarterDays.${value}`)
        }))
      },
      [Frequency.YEAR]: buildYearConfig(t, fiscalStartMonth)
    }),
    [t, fiscalStartMonth]
  );
